import { useState, type FormEvent } from 'react';
import { t } from '../../i18n';
import { Feld, Fehler, InfoTip } from '../../components/ui';
import { useEntityActions } from '../../hooks';
import type { StepProps } from '../CasePage';

const KINDS = ['TECHNISCH', 'FUNKTIONAL', 'OPTISCH', 'NUTZUNGSDAUER', 'SICHERHEIT'];

/**
 * Schritt 5: Auswirkungen (technisch/funktional/optisch/Nutzungsdauer/Sicherheit).
 * Sicherheitsrelevante Befunde lösen die Regel 3.5 aus — sichtbar gemacht,
 * erzwungen wird sie beim Abschluss der optischen Bewertung.
 */
export function Step5Auswirkungen({ caseId, data, reload, readonly }: StepProps) {
  const actions = useEntityActions(caseId, 'impacts', reload);
  const [form, setForm] = useState({ defectFindingId: '', kind: 'TECHNISCH', description: '' });

  async function add(e: FormEvent) {
    e.preventDefault();
    const ok = await actions.create(form);
    if (ok) setForm({ defectFindingId: form.defectFindingId, kind: 'TECHNISCH', description: '' });
  }

  const safetyDefects = data.defectFindings.filter(
    (d: any) => d.safetyRelevant || d.structuralSafetyRelevant,
  );

  return (
    <>
      <section className="karte">
        <h2>
          {t('step5.title')}
          <InfoTip termKey="technischerMinderwert" />
          <InfoTip termKey="optischerMinderwert" />
        </h2>
        <p className="erklaerung">{t('step5.intro')}</p>
        {safetyDefects.length > 0 && (
          <div className="warnung">
            <span className="label">{t('step5.safetyLabel')}</span>
            <p>{t('safety.confirmation.required')}</p>
            <ul>
              {safetyDefects.map((d: any) => (
                <li key={d.id}>{d.title}</li>
              ))}
            </ul>
          </div>
        )}
        {data.defectFindings.length === 0 && <div className="leer-hinweis">{t('step5.noDefects')}</div>}
        {data.defectFindings.map((d: any) => (
          <div className="eintrag" key={d.id}>
            <div className="eintrag-kopf">
              <strong>{d.title}</strong>
            </div>
            {d.impacts.length === 0 ? (
              <p className="meta">{t('step5.empty')}</p>
            ) : (
              <ul>
                {d.impacts.map((im: any) => (
                  <li key={im.id}>
                    <span className="badge">{t(`impact.kind.${im.kind}`)}</span> {im.description}{' '}
                    {!readonly && (
                      <button
                        type="button"
                        className="knopf-sekundaer knopf-klein"
                        onClick={() => actions.remove(im.id)}
                      >
                        {t('common.delete')}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </section>

      {!readonly && data.defectFindings.length > 0 && (
        <section className="karte">
          <h3>{t('step5.add')}</h3>
          <form onSubmit={add}>
            <div className="feld-zeile">
              <Feld label={t('step5.defectRef')} required>
                <select
                  value={form.defectFindingId}
                  onChange={(e) => setForm({ ...form, defectFindingId: e.target.value })}
                  required
                >
                  <option value="">{t('common.search')} …</option>
                  {data.defectFindings.map((d: any) => (
                    <option key={d.id} value={d.id}>
                      {d.title}
                    </option>
                  ))}
                </select>
              </Feld>
              <Feld label={t('step5.kind')} required>
                <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}>
                  {KINDS.map((k) => (
                    <option key={k} value={k}>
                      {t(`impact.kind.${k}`)}
                    </option>
                  ))}
                </select>
              </Feld>
            </div>
            <Feld label={t('common.description')} required error={actions.fieldErrors['description']}>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                required
              />
            </Feld>
            <Fehler code={actions.errorCode} />
            <div className="aktionen">
              <button type="submit" className="knopf-primaer">
                {t('common.add')}
              </button>
            </div>
          </form>
        </section>
      )}
    </>
  );
}
